/**
 * AiChatPanel — Floating AI chat assistant.
 * Bottom-right bubble that expands to a chat panel.
 */
import { useRef, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useCan } from '../../lib/rbac';
import { X, Send, Loader2, Bot, User } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const SUGGESTIONS = [
  '¿Cuánto vendí esta semana?',
  '¿Cuáles son mis productos más rentables?',
  '¿Tengo claims abiertos?',
  '¿Cuántos items tengo activos por sitio?',
  '¿Cuál es mi margen promedio?',
];

export function AiChatPanel() {
  const { sellerId, aiChatOpen: open, toggleAiChat } = useUIStore();
  const isMobile = useIsMobile();
  const can = useCan();
  // Las respuestas del asistente incluyen montos en el texto: sin permiso de ver dinero no se muestra.
  const canMoney = can('global.view_money');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg: Message = { role: 'user', content: msg, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await api.post<any>('/v3/ai/chat', {
        seller_id: sellerId,
        message: msg,
        history: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
      });

      const aiMsg: Message = {
        role: 'assistant',
        content: res.success && res.data?.response ? res.data.response : (res as any).error || 'Error al procesar la consulta.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err.message || 'No se pudo conectar con el asistente'}`,
        timestamp: new Date(),
      }]);
    }
    setLoading(false);
  };

  // El estado `aiChatOpen` puede quedar encendido de antes: la guardia va primero.
  if (!canMoney || !open) return null;

  return (
    <div className={`fixed z-50 bg-[var(--color-bg)] border border-[var(--color-border)] shadow-2xl flex flex-col overflow-hidden ${
      isMobile
        ? 'inset-0 rounded-none'
        : 'bottom-6 right-6 w-[400px] h-[560px] rounded-xl'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[var(--color-primary)] text-white">
        <div className="flex items-center gap-2">
          <Bot size={20} />
          <div>
            <p className="font-semibold text-sm">AI Assistant</p>
            <p className="text-xs opacity-80">CBT Command Center</p>
          </div>
        </div>
        <button onClick={toggleAiChat} className="p-1 rounded hover:bg-white/20"><X size={18} /></button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="space-y-3">
            <div className="text-center py-4">
              <Bot size={32} className="mx-auto text-[var(--color-primary)] mb-2" />
              <p className="text-sm text-[var(--color-text)]">¡Hola! Soy tu asistente AI.</p>
              <p className="text-xs text-[var(--color-text-tertiary)] mt-1">Preguntame sobre tus ventas, productos, márgenes, claims...</p>
            </div>
            <div className="space-y-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  className="w-full text-left px-3 py-2 bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] rounded-lg text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-[var(--color-primary)] flex items-center justify-center shrink-0 mt-0.5">
                  <Bot size={14} className="text-white" />
                </div>
              )}
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-[var(--color-primary)] text-white rounded-br-sm'
                  : 'bg-[var(--color-bg-secondary)] text-[var(--color-text)] border border-[var(--color-border)] rounded-bl-sm'
              }`}>
                {msg.content}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-[var(--color-bg-tertiary)] flex items-center justify-center shrink-0 mt-0.5">
                  <User size={14} className="text-[var(--color-text-secondary)]" />
                </div>
              )}
            </div>
          ))
        )}
        {loading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-full bg-[var(--color-primary)] flex items-center justify-center shrink-0">
              <Bot size={14} className="text-white" />
            </div>
            <div className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg rounded-bl-sm">
              <Loader2 size={16} className="animate-spin text-[var(--color-primary)]" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-[var(--color-border)]">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Preguntame algo..."
            disabled={loading}
            className="flex-1 px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-tertiary)] disabled:opacity-50"
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="px-3 py-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
